import {
  buildSequenceSympyCode,
  buildSubstitutions,
  parseNodeToSympy,
  tryParseClosedIntegerInterval,
  tryParseInfiniteLowerInterval,
  tryParseInfiniteUpperInterval,
} from "@corca/sympy";

import { createLogger } from "@/utils/logger";
import { trySympy } from "@/utils/sympyErrors";

import { collectPriorAssignments, collectPriorDomains } from "./getPhantomHints";

import type { ScopeExpression } from "./scopeExpressions";
import type { HintExecution, PriorAssignment } from "./types";
import type { NodeJson } from "@corca/data";

const logger = createLogger("SequenceHint");

const SEQUENCE_MAX_TERMS = 10;

function findMatrixNode(json: NodeJson): NodeJson | null {
  if (json.type === "matrix") return json;
  for (const child of json.children) {
    const found = findMatrixNode(child);
    if (found) return found;
  }
  return null;
}

function makeEllipsisCell(): NodeJson {
  return {
    type: "inline",
    props: {},
    children: [
      {
        type: "operatorSymbol",
        props: {},
        children: [{ type: "symbol", props: { value: "…", key: "symbol-ellipsis" }, children: [] }],
      },
    ],
  };
}

// The sequence result is an inline > group > matrix; both helpers mutate that matrix in place.
export function appendSequenceEllipsis(json: NodeJson): void {
  const matrix = findMatrixNode(json);
  if (!matrix || matrix.type !== "matrix") return;

  matrix.children.push(makeEllipsisCell());
  matrix.props.cols = (matrix.props.cols ?? matrix.children.length - 1) + 1;
}

export function prependSequenceEllipsis(json: NodeJson): void {
  const matrix = findMatrixNode(json);
  if (!matrix || matrix.type !== "matrix") return;

  matrix.children.unshift(makeEllipsisCell());
  matrix.props.cols = (matrix.props.cols ?? matrix.children.length - 1) + 1;
}

export function applySequenceEllipsis(
  json: NodeJson,
  {
    sequenceTruncated,
    sequenceLeadingEllipsis,
  }: Pick<HintExecution, "sequenceTruncated" | "sequenceLeadingEllipsis">,
): void {
  if (!sequenceTruncated) return;
  if (sequenceLeadingEllipsis) {
    prependSequenceEllipsis(json);
  } else {
    appendSequenceEllipsis(json);
  }
}

// e.g. "a_k" -> { base: "a", subscript: "k" }. Returns null for names without a subscript,
// names carrying superscript/decorator markers, or ambiguous multi-underscore names like
// "a_b_c" (nested subscripts are out of scope; don't mis-split into base "a" / sub "b_c").
export function splitBaseAndSubscript(
  labeledValue: string,
): { base: string; subscript: string } | null {
  if (labeledValue.includes("__sup_") || labeledValue.includes("__dec_")) return null;
  const underscore = labeledValue.indexOf("_");
  if (underscore <= 0 || underscore === labeledValue.length - 1) return null;
  const base = labeledValue.slice(0, underscore);
  const subscript = labeledValue.slice(underscore + 1);
  if (subscript.includes("_")) return null;
  return { base, subscript };
}

export function tryBuildSequenceHint(
  baseName: string,
  scope: ScopeExpression[],
): HintExecution | null {
  const priorAssignments = collectPriorAssignments(scope);

  // Scalar assignment for the same base name wins — defer to the solve path.
  if (priorAssignments.some((a) => a.variableName === baseName)) return null;

  // Match an indexed definition against the bare base name (e.g. "a_k" for "a").
  let indexedDef: PriorAssignment | null = null;
  let subscript: string | null = null;
  for (const assignment of priorAssignments) {
    const split = splitBaseAndSubscript(assignment.variableName);
    if (split?.base === baseName) {
      indexedDef = assignment;
      subscript = split.subscript;
      break;
    }
  }
  if (!indexedDef || !subscript) return null;

  const domains = collectPriorDomains(scope);
  // A variable may carry several domain lines (e.g. `k ∈ [0,2]` then `k ∈ [0,∞)`, or a
  // `k ∈ ℤ` alongside them). Enumeration needs one concrete integer range, and the most
  // recent declaration wins, so scan backwards for the closest enumerable interval.
  const domain = domains
    .get(subscript)
    ?.findLast(
      (d) =>
        tryParseClosedIntegerInterval(d.domainNode) ??
        tryParseInfiniteUpperInterval(d.domainNode) ??
        tryParseInfiniteLowerInterval(d.domainNode),
    );
  if (!domain) return null;

  let lo: number;
  let hi: number;
  let truncated: boolean;
  // A range unbounded below shows a leading "…"; the two other cases only ever show a trailing one.
  let leadingEllipsis = false;
  const closed = tryParseClosedIntegerInterval(domain.domainNode);
  const infiniteUpper = closed ? null : tryParseInfiniteUpperInterval(domain.domainNode);
  const infiniteLower =
    closed || infiniteUpper ? null : tryParseInfiniteLowerInterval(domain.domainNode);
  if (closed) {
    lo = closed.lo;
    truncated = closed.hi - closed.lo + 1 > SEQUENCE_MAX_TERMS;
    hi = truncated ? closed.lo + SEQUENCE_MAX_TERMS - 1 : closed.hi;
  } else if (infiniteUpper) {
    lo = infiniteUpper.lo;
    hi = infiniteUpper.lo + SEQUENCE_MAX_TERMS - 1;
    truncated = true;
  } else if (infiniteLower) {
    // Enumerate the last window ascending; the missing lower terms become the leading "…".
    hi = infiniteLower.hi;
    lo = infiniteLower.hi - SEQUENCE_MAX_TERMS + 1;
    truncated = true;
    leadingEllipsis = true;
  } else {
    return null;
  }

  if (!Number.isSafeInteger(lo) || !Number.isSafeInteger(hi)) return null;

  const variables = new Map<string, string>();
  const bodyInline: NodeJson = { type: "inline", props: {}, children: indexedDef.rhsNodes };
  const parsed = trySympy(() => parseNodeToSympy(bodyInline, variables));
  if ("error" in parsed) return null;
  if (!parsed.expr) return null;
  parsed.warnings.forEach((warning) => logger.warn(warning));

  const indexSymbol = variables.get(subscript);
  if (!indexSymbol) return null;

  // The index is the free enumeration variable; a prior `k = …` line must not
  // substitute it (that would poison both the index arg and the body).
  const otherAssignments = priorAssignments.filter(
    (a) => a.wrapperId !== indexedDef.wrapperId && a.variableName !== subscript,
  );
  const built = trySympy(() => buildSubstitutions(otherAssignments, variables));
  if ("error" in built) return null;
  const { substitutions, usedWrapperIds } = built;
  built.warnings.forEach((warning) => logger.warn(warning));
  substitutions.delete(subscript);

  const code = buildSequenceSympyCode(parsed.expr, variables, {
    indexSymbol,
    lo,
    hi,
    substitutions,
  });

  usedWrapperIds.add(indexedDef.wrapperId);
  usedWrapperIds.add(domain.wrapperId);

  return {
    label: "Sequence",
    command: "sequence",
    pythonCode: code.expr,
    expressionText: baseName,
    hasEagerFunction: false,
    hasMatrixResult: true,
    usedWrapperIds: Array.from(usedWrapperIds),
    inputUnitResult: null,
    symbolNodes: parsed.symbolNodes,
    sequenceTruncated: truncated,
    sequenceLeadingEllipsis: leadingEllipsis,
  };
}
